import { Box, BoxProps, Checkbox, FormControl, FormLabel, Grid, GridItem, HStack, InputGroup, InputRightElement, NumberInput, NumberInputField, Switch, VStack } from '@chakra-ui/react'
import { MlsStatus } from 'features/ListBuilder/domain/ListCriteria/PropertyListCriteria'
import { useMlsBackOnMarketWithinDays, useMlsDaysOnMarket, useMlsDiscountAmount, useMlsDiscountPercent, useMlsExpiredWithinDays, useMlsPendingDays, useMlsPriceChangeWithinDays, useMlsPriceDropPct, useMlsPriceReductionCount, useMlsStatus } from 'features/ListBuilder/infra/react/ListBuilderStore'
import { UseCriteriaHookApi } from 'features/ListBuilder/infra/react/ListBuilderStore/hooks/common'
import { NumberLib } from 'libs/Number'
import { Dollar } from 'libs/dollar'
import { CloseIcon } from 'presentation/components/Icons'
import DetailDisclosurePopover from 'presentation/components/DetailDisclosurePopover'
import { Card, CardHeader, CardSecondaryTitle } from 'presentation/components/molecules/Card'
import ChakraLib from 'presentation/libs/Chakra'
import { makeOptionalRangeCard } from 'presentation/screens/ListBuilderScreen/components/ListBuilderScreenBase/components/OptionalRangeCard'
import { mbp } from 'presentation/utils/mapBreakpoint'
import { ReactNode, useEffect, useState } from 'react'
import { isNumber, isString } from 'remeda'

export const MLSFiltersContent = () => (
  <VStack p={2} spacing={2} align='stretch'>
    <Grid
      gridTemplate={mbp({
        mobSm: 'auto / repeat(1, auto)',
        tabSm: 'auto / repeat(2, 1fr)',
      })}
      gap={2}
    >
      <GridItem>
        <MlsStatusCard h='full' />
      </GridItem>
      <GridItem>
        <ExpiredWithinDaysCard h='full' />
      </GridItem>
      <GridItem>
        <DaysOnMarketCard />
      </GridItem>
      <GridItem>
        <PendingDaysCard />
      </GridItem>
      <GridItem>
        <BackOnMarketWithinDaysCard />
      </GridItem>
      <GridItem>
        <PriceReductionCard />
      </GridItem>
      <GridItem>
        <DiscountBelowAVMCard />
      </GridItem>
    </Grid>
  </VStack>
)

const ON_MARKET_MLS_STATUSES: MlsStatus[] = ['Active', 'Pending']
const OFF_MARKET_MLS_STATUSES: MlsStatus[] = ['Expired', 'Withdrawn', 'Canceled', 'Closed']
const MLS_STATUS_ORDER: MlsStatus[] = [...ON_MARKET_MLS_STATUSES, ...OFF_MARKET_MLS_STATUSES]

const MLS_STATUS_OPTIONS = [
  { label: 'On Market / Actively For Sale', value: 'on-market', statuses: ON_MARKET_MLS_STATUSES },
  { label: 'Off Market / Not Currently For Sale', value: 'off-market', statuses: OFF_MARKET_MLS_STATUSES },
] as const

const MLS_FILTER_HELP_TEXT = {
  mlsStatus: 'Choose whether you want properties that are currently listed for sale or off-market. On-market properties may have more competition but offer transparency. Off-market properties often have less competition and more opportunity for negotiation.',
  failedListings: 'These are properties that were listed but didn\'t sell. Sellers are often frustrated, motivated, and more open to creative or discounted offers. This is one of the most reliable ways to find motivated sellers before they relist or pursue other options.',
  daysOnMarket: 'The longer a home stays unsold, the more pressure and frustration the seller often feels. These sellers may be more flexible on price, terms, or creative solutions, making them strong opportunities for investors looking to negotiate better deals.',
  pendingDays: 'Homes stuck in pending status longer than expected often indicate problems with financing, inspections, or buyer uncertainty. These sellers may welcome backup offers from investors who can close with fewer risks.',
  backOnMarket: 'These properties were under contract but the deal fell through. This often creates urgency and frustration for the seller, especially after the time and stress of preparing to move. Many owners become more flexible and open to faster or more reliable investor offers after a buyer cancels.',
  priceReduction: 'When sellers reduce their price, it often means the property isn\'t getting the response they expected or pressure to sell. Multiple, large, or recent price drops can signal urgency and a higher chance of negotiating a discounted or creative offer.',
  discountedBelowAvm: 'This filter highlights properties priced below estimated market value. While estimates aren\'t perfect, it can quickly surface potential deals and motivated sellers, especially when the discount is significant.',
} as const

const INPUT_DEBOUNCE_MS = 1500

const stopEventPropagation = (ev: { stopPropagation: () => void }) => {
  ev.stopPropagation()
}

const toNumberOrNull = (value: string | number): number | null =>
  isString(value)
    ? NumberLib.fromStringSafe(value)
    : value

const FilterHelpPopover = ({ text }: { text: string }) => (
  <Box
    as='span'
    display='inline-flex'
    alignItems='center'
    ml='1'
    color='accent.blue-to-gray'
    onClick={stopEventPropagation}
    onMouseDown={stopEventPropagation}
    onTouchStart={stopEventPropagation}
  >
    <DetailDisclosurePopover>{text}</DetailDisclosurePopover>
  </Box>
)

const withFilterHelp = (title: string, text: string) => (
  <>
    {title}
    <FilterHelpPopover text={text} />
  </>
)

const MlsStatusCard = (props: BoxProps) => {
  const api = useMlsStatus()
  const hasAnyStatusInGroup = (statuses: MlsStatus[]) =>
    statuses.some(status => api.value.includes(status))
  const isGroupChecked = (statuses: MlsStatus[]) =>
    statuses.every(status => api.value.includes(status))

  return (
    <Card
      variant='default'
      colorScheme='card.bg.1'
      size={mbp({
        mobSm: 'xxs-locked',
        tabSm: 'sm-locked',
      })}
      align='stretch'
      justify='center'
      h='full'
      {...props}
    >
      <CardHeader>
        <CardSecondaryTitle justifySelf='flex-start' flex='1'>
          {withFilterHelp('MLS Status', MLS_FILTER_HELP_TEXT.mlsStatus)}
        </CardSecondaryTitle>
      </CardHeader>

      <VStack mt={2} spacing={1} align='stretch'>
        {MLS_STATUS_OPTIONS.map(option => (
          <Checkbox
            key={option.value}
            colorScheme='neutral'
            isChecked={isGroupChecked(option.statuses)}
            isIndeterminate={
              hasAnyStatusInGroup(option.statuses)
              && !isGroupChecked(option.statuses)
            }
            onChange={() => {
              const shouldRemove = isGroupChecked(option.statuses)
              const nextValue = shouldRemove
                ? api.value.filter(status => !option.statuses.includes(status))
                : MLS_STATUS_ORDER
                  .filter(status =>
                    api.value.includes(status)
                    || option.statuses.includes(status),
                  )
              api.apply(nextValue)
            }}
          >
            {option.label}
          </Checkbox>
        ))}
      </VStack>
    </Card>
  )
}

const makeOptionalNumberCard = ({
  title,
  displayNamePrefix,
  label,
  useApi,
  min,
  defaultOnEnable,
  placeholder,
  formatInputDisplay,
}: {
  title: ReactNode
  displayNamePrefix: string
  label: string
  useApi: () => UseCriteriaHookApi<number | null>
  min?: number
  defaultOnEnable?: number
  placeholder?: string
  formatInputDisplay?: (value: string | number) => string
}) => {
  const OptionalNumberCard = (props: BoxProps) => {
    const api = useApi()
    const [isOpen, setIsOpen] = useState(isNumber(api.value))
    const [value, setValue] = useState<string | number>(api.value ?? '')

    useEffect(() => {
      setValue(api.value ?? '')
    }, [api.value])

    useEffect(() => {
      if (!isOpen) return

      const nextValue = toNumberOrNull(value)
      const currentValue = api.value ?? null

      if (nextValue === currentValue) return

      const timeoutId = window.setTimeout(() => {
        api.apply(nextValue)
      }, INPUT_DEBOUNCE_MS)

      return () => {
        window.clearTimeout(timeoutId)
      }
    }, [api.apply, api.value, isOpen, value])

    return (
      <Card
        variant='default'
        colorScheme='card.bg.1'
        size={mbp({
          mobSm: 'xxs-locked',
          tabSm: 'sm-locked',
        })}
        align='stretch'
        justify='center'
        bg={isOpen ? 'neutral.200' : undefined}
        h='full'
        onClick={ev => {
          if (isOpen) return
          if (ChakraLib.isTargetChakraSwitch(ev.target)) return
          setIsOpen(true)
        }}
        {...props}
      >
        <CardHeader>
          <HStack>
            <CardSecondaryTitle justifySelf='flex-start' flex='1'>
              {title}
            </CardSecondaryTitle>
            <Switch
              colorScheme='default'
              isChecked={isOpen}
              onChange={() => {
                setIsOpen(!isOpen)
                const willOpen = !isOpen

                if (willOpen) {
                  const initialValue = api.value ?? defaultOnEnable ?? min ?? 0
                  api.apply(initialValue)
                  setValue(initialValue)
                } else {
                  api.apply(null)
                  setValue('')
                }
              }}
            />
          </HStack>
        </CardHeader>

        {isOpen && (
          <FormControl mt={2}>
            <FormLabel>{label}</FormLabel>
            <NumberInput
              min={min}
              keepWithinRange
              precision={0}
              value={value}
              format={formatInputDisplay}
              onChange={setValue}
              onBlur={() => {
                api.apply(toNumberOrNull(value))
              }}
            >
              <InputGroup>
                <NumberInputField placeholder={placeholder} />
                {value && (
                  <InputRightElement
                    role='button'
                    px={1}
                    onClick={() => {
                      setValue('')
                      api.apply(null)
                    }}
                  >
                    <CloseIcon />
                  </InputRightElement>
                )}
              </InputGroup>
            </NumberInput>
          </FormControl>
        )}
      </Card>
    )
  }

  OptionalNumberCard.displayName = `${displayNamePrefix}Card`
  return OptionalNumberCard
}

const formatDollarWithCommas = (value: string | number) => {
  if (value === '-') return '-$'

  const maybeNumber = isNumber(value)
    ? value
    : NumberLib.fromStringSafe(value)

  if (maybeNumber === null) return '$'

  return Dollar.formatNumberWithCommas(maybeNumber)
}

const ExpiredWithinDaysCard = makeOptionalNumberCard({
  title: withFilterHelp('Failed Listing / Didn\'t Sell', MLS_FILTER_HELP_TEXT.failedListings),
  displayNamePrefix: 'ExpiredWithinDays',
  label: 'Failed to sell within the last ___ days',
  useApi: useMlsExpiredWithinDays,
  min: 1,
  defaultOnEnable: 30,
  placeholder: '30',
})

const DaysOnMarketCard = makeOptionalRangeCard({
  title: withFilterHelp('Days on Market', MLS_FILTER_HELP_TEXT.daysOnMarket),
  displayNamePrefix: 'MlsDaysOnMarket',
  useApi: useMlsDaysOnMarket,
  minLabel: 'For sale at least ___ days',
  maxLabel: 'For sale at most ___ days',
  minPlaceholder: '0',
  maxPlaceholder: '365',
})

const PendingDaysCard = () => {
  const api = useMlsPendingDays()
  const [isOpen, setIsOpen] = useState(isNumber(api.value[0]) || isNumber(api.value[1]))
  const [value, setValue] = useState<string | number>(api.value[0] ?? '')

  useEffect(() => {
    setValue(api.value[0] ?? '')
  }, [api.value[0]])

  // Remove legacy max value now that this filter is min-only.
  useEffect(() => {
    if (!isNumber(api.value[1])) return
    api.apply([api.value[0], null])
  }, [api.value[0], api.value[1]])

  useEffect(() => {
    if (!isOpen) return

    const nextMinValue = toNumberOrNull(value)
    const [currentMinValue, currentMaxValue] = api.value

    if (nextMinValue === currentMinValue && currentMaxValue === null) return

    const timeoutId = window.setTimeout(() => {
      api.apply([nextMinValue, null])
    }, INPUT_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [api.apply, api.value[0], api.value[1], isOpen, value])

  return (
    <Card
      variant='default'
      colorScheme='card.bg.1'
      size={mbp({
        mobSm: 'xxs-locked',
        tabSm: 'sm-locked',
      })}
      align='stretch'
      justify='center'
      bg={isOpen ? 'neutral.200' : undefined}
      h='full'
      onClick={ev => {
        if (isOpen) return
        if (ChakraLib.isTargetChakraSwitch(ev.target)) return
        setIsOpen(true)
      }}
    >
      <CardHeader>
        <HStack>
          <CardSecondaryTitle justifySelf='flex-start' flex='1'>
            {withFilterHelp('Potential Closing Problem', MLS_FILTER_HELP_TEXT.pendingDays)}
          </CardSecondaryTitle>
          <Switch
            colorScheme='default'
            isChecked={isOpen}
            onChange={() => {
              setIsOpen(!isOpen)
              const willClose = isOpen
              if (!willClose) return
              api.apply([null, null])
              setValue('')
            }}
          />
        </HStack>
      </CardHeader>

      {isOpen && (
        <FormControl mt={2}>
          <FormLabel>Pending closing at least ____ days</FormLabel>
          <NumberInput
            keepWithinRange
            min={0}
            precision={0}
            value={value}
            onChange={setValue}
            onBlur={() => {
              api.apply([
                toNumberOrNull(value),
                null,
              ])
            }}
          >
            <InputGroup>
              <NumberInputField placeholder='0' />
              {value && (
                <InputRightElement
                  role='button'
                  px={1}
                  onClick={() => {
                    setValue('')
                    api.apply([null, null])
                  }}
                >
                  <CloseIcon />
                </InputRightElement>
              )}
            </InputGroup>
          </NumberInput>
        </FormControl>
      )}
    </Card>
  )
}

const BackOnMarketWithinDaysCard = makeOptionalRangeCard({
  title: withFilterHelp('Buyer Canceled / Back On Market', MLS_FILTER_HELP_TEXT.backOnMarket),
  displayNamePrefix: 'MlsBackOnMarketWithinDays',
  useApi: useMlsBackOnMarketWithinDays,
  minLabel: 'Back on market at least ___ days ago',
  maxLabel: 'Back on market at most ___ days ago',
  minPlaceholder: '0',
  maxPlaceholder: '365',
})

const PriceReductionCard = () => {
  const dropPctApi = useMlsPriceDropPct()
  const reductionCountApi = useMlsPriceReductionCount()
  const changeWithinDaysApi = useMlsPriceChangeWithinDays()

  const [dropPctMin, setDropPctMin] = useState<string | number>(dropPctApi.value[0] ?? '')
  const [reductionCountMin, setReductionCountMin] = useState<string | number>(reductionCountApi.value[0] ?? '')
  const [changeMinDays, setChangeMinDays] = useState<string | number>(changeWithinDaysApi.value[0] ?? '')
  const [changeMaxDays, setChangeMaxDays] = useState<string | number>(changeWithinDaysApi.value[1] ?? '')

  const hasAnyValue = [
    ...dropPctApi.value,
    ...reductionCountApi.value,
    ...changeWithinDaysApi.value,
  ].some(isNumber)

  const [isOpen, setIsOpen] = useState(hasAnyValue)

  useEffect(() => {
    setDropPctMin(dropPctApi.value[0] ?? '')
  }, [dropPctApi.value[0]])

  useEffect(() => {
    setReductionCountMin(reductionCountApi.value[0] ?? '')
  }, [reductionCountApi.value[0]])

  useEffect(() => {
    setChangeMinDays(changeWithinDaysApi.value[0] ?? '')
    setChangeMaxDays(changeWithinDaysApi.value[1] ?? '')
  }, [changeWithinDaysApi.value[0], changeWithinDaysApi.value[1]])

  // Remove legacy max values for these criteria now that the UI is min-only.
  useEffect(() => {
    if (!isNumber(dropPctApi.value[1])) return
    dropPctApi.apply([dropPctApi.value[0], null])
  }, [dropPctApi.value[0], dropPctApi.value[1]])

  useEffect(() => {
    if (!isNumber(reductionCountApi.value[1])) return
    reductionCountApi.apply([reductionCountApi.value[0], null])
  }, [reductionCountApi.value[0], reductionCountApi.value[1]])

  useEffect(() => {
    if (!isOpen) return

    const nextDropPctMin = toNumberOrNull(dropPctMin)
    const [currentDropPctMin, currentDropPctMax] = dropPctApi.value

    if (nextDropPctMin === currentDropPctMin && currentDropPctMax === null) return

    const timeoutId = window.setTimeout(() => {
      dropPctApi.apply([nextDropPctMin, null])
    }, INPUT_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [dropPctApi.apply, dropPctApi.value[0], dropPctApi.value[1], dropPctMin, isOpen])

  useEffect(() => {
    if (!isOpen) return

    const nextReductionCountMin = toNumberOrNull(reductionCountMin)
    const [currentReductionCountMin, currentReductionCountMax] = reductionCountApi.value

    if (
      nextReductionCountMin === currentReductionCountMin
      && currentReductionCountMax === null
    ) return

    const timeoutId = window.setTimeout(() => {
      reductionCountApi.apply([nextReductionCountMin, null])
    }, INPUT_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    isOpen,
    reductionCountApi.apply,
    reductionCountApi.value[0],
    reductionCountApi.value[1],
    reductionCountMin,
  ])

  useEffect(() => {
    if (!isOpen) return

    const nextMinDays = toNumberOrNull(changeMinDays)
    const nextMaxDays = toNumberOrNull(changeMaxDays)
    const [currentMinDays, currentMaxDays] = changeWithinDaysApi.value

    if (nextMinDays === currentMinDays && nextMaxDays === currentMaxDays) return

    const timeoutId = window.setTimeout(() => {
      changeWithinDaysApi.apply([nextMinDays, nextMaxDays])
    }, INPUT_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    changeMaxDays,
    changeMinDays,
    changeWithinDaysApi.apply,
    changeWithinDaysApi.value[0],
    changeWithinDaysApi.value[1],
    isOpen,
  ])

  const applyChangeWithinDays = () => {
    changeWithinDaysApi.apply([
      toNumberOrNull(changeMinDays),
      toNumberOrNull(changeMaxDays),
    ])
  }

  return (
    <Card
      variant='default'
      colorScheme='card.bg.1'
      size={mbp({
        mobSm: 'xxs-locked',
        tabSm: 'sm-locked',
      })}
      align='stretch'
      justify='center'
      bg={isOpen ? 'neutral.200' : undefined}
      h='full'
      onClick={ev => {
        if (isOpen) return
        if (ChakraLib.isTargetChakraSwitch(ev.target)) return
        setIsOpen(true)
      }}
    >
      <CardHeader>
        <HStack>
          <CardSecondaryTitle justifySelf='flex-start' flex='1'>
            {withFilterHelp('Price Reduction', MLS_FILTER_HELP_TEXT.priceReduction)}
          </CardSecondaryTitle>
          <Switch
            colorScheme='default'
            isChecked={isOpen}
            onChange={() => {
              setIsOpen(!isOpen)
              const willClose = isOpen
              if (!willClose) return

              dropPctApi.apply([null, null])
              reductionCountApi.apply([null, null])
              changeWithinDaysApi.apply([null, null])
              setDropPctMin('')
              setReductionCountMin('')
              setChangeMinDays('')
              setChangeMaxDays('')
            }}
          />
        </HStack>
      </CardHeader>

      {isOpen && (
        <Grid
          mt={2}
          gridTemplate={mbp({
            mobSm: 'auto / 1fr',
            tabSm: 'auto / repeat(2, 1fr)',
          })}
          gap={1}
        >
          <GridItem>
            <FormControl>
              <FormLabel>Total Price Drop At Least ___ %</FormLabel>
              <NumberInput
                keepWithinRange
                min={0}
                max={100}
                precision={0}
                value={dropPctMin}
                onChange={setDropPctMin}
                onBlur={() => {
                  dropPctApi.apply([toNumberOrNull(dropPctMin), null])
                }}
              >
                <InputGroup>
                  <NumberInputField placeholder='0' />
                  {dropPctMin && (
                    <InputRightElement
                      role='button'
                      px={1}
                      onClick={() => {
                        setDropPctMin('')
                        dropPctApi.apply([null, null])
                      }}
                    >
                      <CloseIcon />
                    </InputRightElement>
                  )}
                </InputGroup>
              </NumberInput>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel>Price Dropped At Least ___ Times</FormLabel>
              <NumberInput
                keepWithinRange
                min={0}
                precision={0}
                value={reductionCountMin}
                onChange={setReductionCountMin}
                onBlur={() => {
                  reductionCountApi.apply([toNumberOrNull(reductionCountMin), null])
                }}
              >
                <InputGroup>
                  <NumberInputField placeholder='0' />
                  {reductionCountMin && (
                    <InputRightElement
                      role='button'
                      px={1}
                      onClick={() => {
                        setReductionCountMin('')
                        reductionCountApi.apply([null, null])
                      }}
                    >
                      <CloseIcon />
                    </InputRightElement>
                  )}
                </InputGroup>
              </NumberInput>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel>Price Dropped At Least ___ Days Ago</FormLabel>
              <NumberInput
                keepWithinRange
                min={0}
                max={isString(changeMaxDays) ? NumberLib.fromStringSafe(changeMaxDays) ?? undefined : changeMaxDays ?? undefined}
                precision={0}
                value={changeMinDays}
                onChange={setChangeMinDays}
                onBlur={applyChangeWithinDays}
              >
                <InputGroup>
                  <NumberInputField placeholder='0' />
                  {changeMinDays && (
                    <InputRightElement
                      role='button'
                      px={1}
                      onClick={() => {
                        setChangeMinDays('')
                        changeWithinDaysApi.apply([null, toNumberOrNull(changeMaxDays)])
                      }}
                    >
                      <CloseIcon />
                    </InputRightElement>
                  )}
                </InputGroup>
              </NumberInput>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel>Price Dropped At Most ____ Days Ago</FormLabel>
              <NumberInput
                keepWithinRange
                min={isString(changeMinDays) ? NumberLib.fromStringSafe(changeMinDays) ?? undefined : changeMinDays ?? undefined}
                precision={0}
                value={changeMaxDays}
                onChange={setChangeMaxDays}
                onBlur={applyChangeWithinDays}
              >
                <InputGroup>
                  <NumberInputField placeholder='365' />
                  {changeMaxDays && (
                    <InputRightElement
                      role='button'
                      px={1}
                      onClick={() => {
                        setChangeMaxDays('')
                        changeWithinDaysApi.apply([toNumberOrNull(changeMinDays), null])
                      }}
                    >
                      <CloseIcon />
                    </InputRightElement>
                  )}
                </InputGroup>
              </NumberInput>
            </FormControl>
          </GridItem>
        </Grid>
      )}
    </Card>
  )
}

const DiscountBelowAVMCard = () => {
  const discountPercentApi = useMlsDiscountPercent()
  const discountAmountApi = useMlsDiscountAmount()

  const [discountPercentMin, setDiscountPercentMin] = useState<string | number>(discountPercentApi.value[0] ?? '')
  const [discountAmountMin, setDiscountAmountMin] = useState<string | number>(discountAmountApi.value[0] ?? '')

  const hasAnyValue = [
    ...discountPercentApi.value,
    ...discountAmountApi.value,
  ].some(isNumber)

  const [isOpen, setIsOpen] = useState(hasAnyValue)

  useEffect(() => {
    setDiscountPercentMin(discountPercentApi.value[0] ?? '')
  }, [discountPercentApi.value[0]])

  useEffect(() => {
    setDiscountAmountMin(discountAmountApi.value[0] ?? '')
  }, [discountAmountApi.value[0]])

  // Remove legacy max values for discount filters now that the UI is min-only.
  useEffect(() => {
    if (!isNumber(discountPercentApi.value[1])) return
    discountPercentApi.apply([discountPercentApi.value[0], null])
  }, [discountPercentApi.value[0], discountPercentApi.value[1]])

  useEffect(() => {
    if (!isNumber(discountAmountApi.value[1])) return
    discountAmountApi.apply([discountAmountApi.value[0], null])
  }, [discountAmountApi.value[0], discountAmountApi.value[1]])

  useEffect(() => {
    if (!isOpen) return

    const nextDiscountPercentMin = toNumberOrNull(discountPercentMin)
    const [currentDiscountPercentMin, currentDiscountPercentMax] = discountPercentApi.value

    if (
      nextDiscountPercentMin === currentDiscountPercentMin
      && currentDiscountPercentMax === null
    ) return

    const timeoutId = window.setTimeout(() => {
      discountPercentApi.apply([nextDiscountPercentMin, null])
    }, INPUT_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    discountPercentApi.apply,
    discountPercentApi.value[0],
    discountPercentApi.value[1],
    discountPercentMin,
    isOpen,
  ])

  useEffect(() => {
    if (!isOpen) return

    const nextDiscountAmountMin = toNumberOrNull(discountAmountMin)
    const [currentDiscountAmountMin, currentDiscountAmountMax] = discountAmountApi.value

    if (
      nextDiscountAmountMin === currentDiscountAmountMin
      && currentDiscountAmountMax === null
    ) return

    const timeoutId = window.setTimeout(() => {
      discountAmountApi.apply([nextDiscountAmountMin, null])
    }, INPUT_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    discountAmountApi.apply,
    discountAmountApi.value[0],
    discountAmountApi.value[1],
    discountAmountMin,
    isOpen,
  ])

  return (
    <Card
      variant='default'
      colorScheme='card.bg.1'
      size={mbp({
        mobSm: 'xxs-locked',
        tabSm: 'sm-locked',
      })}
      align='stretch'
      justify='center'
      bg={isOpen ? 'neutral.200' : undefined}
      h='full'
      onClick={ev => {
        if (isOpen) return
        if (ChakraLib.isTargetChakraSwitch(ev.target)) return
        setIsOpen(true)
      }}
    >
      <CardHeader>
        <HStack>
          <CardSecondaryTitle justifySelf='flex-start' flex='1'>
            {withFilterHelp('Listed Below Estimated Value', MLS_FILTER_HELP_TEXT.discountedBelowAvm)}
          </CardSecondaryTitle>
          <Switch
            colorScheme='default'
            isChecked={isOpen}
            onChange={() => {
              setIsOpen(!isOpen)
              const willClose = isOpen
              if (!willClose) return

              discountPercentApi.apply([null, null])
              discountAmountApi.apply([null, null])
              setDiscountPercentMin('')
              setDiscountAmountMin('')
            }}
          />
        </HStack>
      </CardHeader>

      {isOpen && (
        <Grid
          mt={2}
          gridTemplate={mbp({
            mobSm: 'auto / 1fr',
            tabSm: 'auto / repeat(2, 1fr)',
          })}
          gap={1}
        >
          <GridItem>
            <FormControl>
              <FormLabel>Minimum Discount %</FormLabel>
              <NumberInput
                keepWithinRange
                min={0}
                max={100}
                precision={0}
                value={discountPercentMin}
                onChange={setDiscountPercentMin}
                onBlur={() => {
                  discountPercentApi.apply([toNumberOrNull(discountPercentMin), null])
                }}
              >
                <InputGroup>
                  <NumberInputField placeholder='0' />
                  {discountPercentMin && (
                    <InputRightElement
                      role='button'
                      px={1}
                      onClick={() => {
                        setDiscountPercentMin('')
                        discountPercentApi.apply([null, null])
                      }}
                    >
                      <CloseIcon />
                    </InputRightElement>
                  )}
                </InputGroup>
              </NumberInput>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel>Minimum Discount $</FormLabel>
              <NumberInput
                keepWithinRange
                min={0}
                precision={0}
                format={formatDollarWithCommas}
                value={discountAmountMin}
                onChange={setDiscountAmountMin}
                onBlur={() => {
                  discountAmountApi.apply([toNumberOrNull(discountAmountMin), null])
                }}
              >
                <InputGroup>
                  <NumberInputField placeholder='$' />
                  {discountAmountMin && (
                    <InputRightElement
                      role='button'
                      px={1}
                      onClick={() => {
                        setDiscountAmountMin('')
                        discountAmountApi.apply([null, null])
                      }}
                    >
                      <CloseIcon />
                    </InputRightElement>
                  )}
                </InputGroup>
              </NumberInput>
            </FormControl>
          </GridItem>
        </Grid>
      )}
    </Card>
  )
}
